import { useCallback, useMemo } from 'react';
import type { NotificationPermissionsStatus } from 'expo-notifications';

import {
  type PermissionsMachineImplementations,
  useInitPermissionsMachine,
  usePermissionsMachine,
} from '@sg/shared-fe-features-permissions';

import type { FeatureFlagTypes } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { usePermissionsPrimerExperiment } from '../usePermissionsPrimerExperiment';
import { usePushNotificationPermissionPrimerEnabled } from '../usePushNotificationPermissionPrimerEnabled';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Drives the push notification permission primer flow for a specific entry
 * point. `variants` is the FF allow-list this consumer responds to.
 *
 * Returns the flags + handlers the consumer needs to decide whether to render
 * a loading screen, the primer, or its normal content.
 */
export const usePushNotificationPermissionPrimer = (params: Params) => {
  const { variants } = params;

  const { track } = useTelemetry();
  const { canEnablePushNotificationPrimer, pushNotificationPrimerVariant } =
    usePushNotificationPermissionPrimerEnabled();
  const { trackExperimentStarted } = usePermissionsPrimerExperiment();

  // ─────────────────────────────────────────────────────────────────────

  const isPushNotificationPrimerEnabled =
    canEnablePushNotificationPrimer &&
    (pushNotificationPrimerVariant === 'disabled'
      ? false
      : variants.includes(pushNotificationPrimerVariant));

  // ─── Telemetry ───────────────────────────────────────────────────────

  const entryPoint = PUSH_PRIMER_ENTRY_POINT[pushNotificationPrimerVariant];

  const telemetryPayload = useMemo(
    () => ({ entry_point: entryPoint, variant: pushNotificationPrimerVariant }),
    [entryPoint, pushNotificationPrimerVariant],
  );

  const onPushNotificationPrimerShow = useCallback(() => {
    trackExperimentStarted();
    track('push_notification_primer_shown', telemetryPayload);
  }, [telemetryPayload, track, trackExperimentStarted]);

  const onRequestPushNotificationPermission = useCallback(() => {
    track('push_notification_primer_cta_tapped', telemetryPayload);
  }, [telemetryPayload, track]);

  const onSkipPushNotificationPermission = useCallback(() => {
    track('push_notification_primer_dismissed', { ...telemetryPayload, action: 'not_now' });
  }, [telemetryPayload, track]);

  const onPushNotificationPermissionGranted = useCallback(
    (_args: unknown, _permissionResponse: NotificationPermissionsStatus) => {
      track('push_permission_granted', telemetryPayload);
    },
    [telemetryPayload, track],
  );

  const onPushNotificationPermissionDenied = useCallback(
    (_args: unknown, _permissionResponse?: NotificationPermissionsStatus) => {
      track('push_permission_denied', telemetryPayload);
    },
    [telemetryPayload, track],
  );

  const implementations = useMemo<PermissionsMachineImplementations>(
    () => ({
      actions: {
        onPushNotificationPrimerShow,
        onRequestPushNotificationPermission,
        onSkipPushNotificationPermission,
        onPushNotificationPermissionGranted,
        onPushNotificationPermissionDenied,
      },
    }),
    [
      onPushNotificationPermissionDenied,
      onPushNotificationPermissionGranted,
      onPushNotificationPrimerShow,
      onRequestPushNotificationPermission,
      onSkipPushNotificationPermission,
    ],
  );

  // ─── Actor ───────────────────────────────────────────────────────────

  const permissionsActorRef = useInitPermissionsMachine({
    isPushNotificationPrimerEnabled,
    implementations,
  });

  const {
    isPushNotificationPermissionsLoading,
    isPushNotificationPrimerVisible,
    isRequestingPushNotificationPermission,
    isSkippingPushNotificationPermission,
    isPushNotificationPermissionDenied,
    isPushNotificationPermissionSkipped,
    isPushNotificationPermissionAlreadyShown,
    requestPushNotificationPermissions,
    skipPushNotificationPermissions,
  } = usePermissionsMachine(permissionsActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isPrimerActive =
    isPushNotificationPrimerVisible ||
    isRequestingPushNotificationPermission ||
    isSkippingPushNotificationPermission;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isPushNotificationPrimerEnabled,
    isPushNotificationPermissionsLoading,
    isPrimerActive,
    isRequestingPushNotificationPermission,
    isSkippingPushNotificationPermission,
    isPushNotificationPermissionDenied,
    isPushNotificationPermissionSkipped,
    isPushNotificationPermissionAlreadyShown,
    onAllow: requestPushNotificationPermissions,
    onSkip: skipPushNotificationPermissions,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PUSH_PRIMER_ENTRY_POINT = {
  disabled: undefined,
  A: 'onboarding',
  B: 'location-search',
} as const satisfies Record<FeatureFlagTypes.LocationPrimerVariation, string | undefined>;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  variants: Exclude<
    ReturnType<typeof usePushNotificationPermissionPrimerEnabled>['pushNotificationPrimerVariant'],
    'disabled'
  >[];
};
